import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import PageHero from '../components/PageHero';
import { API_URL, getMediaURL } from '../config';

const About = () => {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [heroData, setHeroData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await Promise.all([getAboutData(), getHeroData()]);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getHeroData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/heroes?populate=*`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      if (result.data && result.data[0]) {
        const heroItem = result.data[0];
        setHeroData({
          title: 'Hakkımda',
          subtitle: 'Profesyonel Deneyim ve Uzmanlık',
          image: heroItem.image?.formats?.large?.url ? 
            getMediaURL(heroItem.image.formats.large.url) : null
        });
      }
    } catch (error) {
      console.error('Error fetching hero:', error);
    }
  };

  const getAboutData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/hakkimdas?populate=*`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.data || result.data.length === 0) {
        throw new Error('Veri bulunamadı');
      }

      const firstItem = result.data[0];
      console.log('Processing item:', firstItem);

      const processedData = {
        name: firstItem.name,
        title: firstItem.title,
        description: firstItem.description,
        content: firstItem.content,
        linkedin: firstItem.linkedin,
        image: firstItem.image,
        cv: firstItem.cv
      };

      console.log('Processed data:', processedData);
      setAboutData(processedData);
      setError(null);
    } catch (error) {
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });
      setError(`Veri yüklenirken bir hata oluştu: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCV = () => {
    if (aboutData?.cv?.url) {
      const cvUrl = getMediaURL(aboutData.cv.url);
      window.open(cvUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  // Debug için
  console.log('Rendering with data:', aboutData);

  return (
    <div className="min-h-screen bg-gray-100">
      {heroData && (
        <PageHero
          title={heroData.title}
          subtitle={heroData.subtitle}
          image={heroData.image}
        />
      )}
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            {/* Profil Bölümü */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
              <div className="p-8">
                {/* Fotoğraf ve İsim Bölümü */}
                <div className="flex flex-col md:flex-row items-center mb-8">
                  <div className="w-48 h-48 mb-6 md:mb-0 md:mr-8 relative">
                    <div className="w-full h-full rounded-full overflow-hidden border-4 border-[#800020] shadow-lg">
                      {aboutData?.image?.url && (
                        <img
                          src={getMediaURL(aboutData.image.url)}
                          alt={aboutData?.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    {/* Dekoratif Arka Plan Elemanı */}
                    <div className="absolute -top-2 -left-2 w-full h-full rounded-full border-2 border-[#800020] -z-10"></div>
                    <div className="absolute -bottom-2 -right-2 w-full h-full rounded-full border-2 border-[#800020] -z-10"></div>
                  </div>
                  <div className="text-center md:text-left flex-1">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{aboutData?.name}</h1>
                    <p className="text-xl text-gray-600 mb-4">{aboutData?.title}</p>
                    <div className="text-gray-600 mb-6">
                      <ReactMarkdown>{aboutData?.description || ''}</ReactMarkdown>
                    </div>
                    
                    {/* CV ve LinkedIn Butonları */}
                    <div className="flex flex-wrap justify-center md:justify-start gap-4">
                      {aboutData?.cv?.url && (
                        <button
                          onClick={handleDownloadCV}
                          className="flex items-center bg-white border-2 border-[#800020] text-[#800020] px-6 py-3 rounded-lg hover:bg-[#800020] hover:text-white transition-all duration-300 shadow-md"
                        >
                          <svg 
                            className="w-5 h-5 mr-2" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth={2} 
                              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                            />
                          </svg>
                          CV İndir
                        </button>
                      )}
                      
                      {aboutData?.linkedin && (
                        <a
                          href={aboutData.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center bg-white border-2 border-[#0077B5] text-[#0077B5] px-6 py-3 rounded-lg hover:bg-[#0077B5] hover:text-white transition-all duration-300 shadow-md"
                        >
                          <svg 
                            className="w-5 h-5 mr-2" 
                            fill="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                          </svg>
                          LinkedIn
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-8">
                  <div className="prose max-w-none">
                    <ReactMarkdown>{aboutData?.content || ''}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
